import {Show} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';

/** Length of the small boundary tick under the baseline in band mode. */
const BAND_BOUNDARY_TICK_LENGTH = 4;

export interface AxisTick {
  tickX: number;
  priceLabel: string;
  percentLabel: number;
}

interface DistributionAxisProps extends TestIdProps {
  width: number;
  baselineY: number;
  ticks: AxisTick[];
  /**
   * X positions of every band boundary — small ticks keep even the boundaries
   * whose labels were thinned out visible. Empty on the linear axis.
   */
  boundaryTickXs: number[];
  /** Band mode explains its cumulative percents with a native hover tooltip. */
  isBandMode: boolean;
}

/** Baseline with the two-row (price / cumulative percent) axis labels. */
export function DistributionAxis(props: DistributionAxisProps) {
  const theme = useTheme();

  return (
    <g data-testid={suffixTestId('axis', props)}>
      <line
        x1={0}
        y1={props.baselineY}
        x2={props.width}
        y2={props.baselineY}
        stroke={theme.colors.palettes.neutral[30][100]}
      />
      {props.boundaryTickXs.map((tickX) => (
        <line
          key={tickX}
          x1={tickX}
          y1={props.baselineY}
          x2={tickX}
          y2={props.baselineY + BAND_BOUNDARY_TICK_LENGTH}
          stroke={theme.colors.palettes.neutral[30][100]}
        />
      ))}
      {props.ticks.map((tick) => (
        <g key={tick.tickX}>
          <text
            x={tick.tickX}
            y={props.baselineY + 16}
            fontSize={theme.fontSizes.text.xxSmall}
            fontWeight={theme.fontWeights.text.default}
            fill={theme.colors.text.tertiary}
            textAnchor="middle"
          >
            {tick.priceLabel}
          </text>
          <text
            x={tick.tickX}
            y={props.baselineY + 29}
            fontSize={theme.fontSizes.text.xxSmall}
            fontWeight={theme.fontWeights.text.alternative}
            fill={theme.colors.text.secondary}
            textAnchor="middle"
          >
            {/* Native SVG hover tooltip — band boundaries are portal steps, so the
                cumulative semantics is not obvious the way exact fifths are */}
            <Show when={props.isBandMode}>
              <title>{i18n.t('entity.overviewChart.labels.priceDistributionBandsAxisHint')}</title>
            </Show>
            {`${tick.percentLabel} %`}
          </text>
        </g>
      ))}
    </g>
  );
}
